import React from 'react';
import './App.css';
import useInsightsState from './hooks/useInsightsState';
import WorkflowProgress from './components/common/WorkflowProgress';
import Notification from './components/common/Notification';
import Dashboard from './components/dashboard/Dashboard';
import DeepDiveView from './components/workflow/DeepDiveView';
import ContentMatchView from './components/workflow/ContentMatchView';
import ScoringView from './components/workflow/ScoringView';
import DecisionView from './components/workflow/DecisionView';
import BriefView from './components/workflow/BriefView';
import AudienceBriefModal from './components/modals/AudienceBriefModal';
import MaiaModal from './components/modals/MaiaModal';
import QuickMaiaModal from './components/modals/QuickMaiaModal';
import PackageDetailView from './components/packages/PackageDetailView';

const InsightsAgentPrototype = () => {
  const state = useInsightsState();

  const {
    activeView,
    workflowStep,
    opportunityScore,
    briefData,
    showMaia,
    selectedTrend,
    showQuickMaia,
    notification,
    clearNotification,
    setActiveView,
    setSelectedTrend,
    setWorkflowStep,
    setOpportunityScore,
    setBriefData,
    setSavedResearch,
    setSelectedPackage,
    setShowQuickMaia,
  } = state;

  const renderWorkflowContent = () => {
    if (workflowStep === 1) return <DeepDiveView state={state} />;
    if (workflowStep === 2) return <ContentMatchView state={state} />;
    if (workflowStep === 3) return <ScoringView state={state} />;
    if (workflowStep === 4 && opportunityScore) return <DecisionView state={state} />;
    if (workflowStep === 5 && briefData) return <BriefView state={state} />;
    return <Dashboard state={state} />;
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0f0f1a',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color: '#f3f4f6'
    }}>
      {/* Header */}
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 32px',
        backgroundColor: '#1a1a2e',
        borderBottom: '1px solid #2d2d44'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Back to Dashboard button - shown when not on dashboard */}
          {activeView !== 'dashboard' && (
            <button
              onClick={() => {
                setActiveView('dashboard');
                setSelectedTrend(null);
                setWorkflowStep(0);
                setOpportunityScore(null);
                setBriefData(null);
                setSavedResearch([]);
                setSelectedPackage(null);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                backgroundColor: '#252540',
                border: '1px solid #2d2d44',
                borderRadius: 6,
                color: '#9ca3af',
                fontSize: 13,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#2d2d44';
                e.currentTarget.style.color = '#f3f4f6';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#252540';
                e.currentTarget.style.color = '#9ca3af';
              }}
            >
              <span style={{ fontSize: 16 }}>{'\u2190'}</span>
              Dashboard
            </button>
          )}
          <div
            onClick={() => {
              setActiveView('dashboard');
              setSelectedTrend(null);
              setWorkflowStep(0);
              setOpportunityScore(null);
              setBriefData(null);
              setSavedResearch([]);
              setSelectedPackage(null);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer'
            }}
          >
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
              fontSize: 18
            }}>
              IA
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: '600' }}>Insights Agent</h1>
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>Trend Opportunity Engine</p>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Prominent MAIA Button */}
          <button
            onClick={() => setShowQuickMaia(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              border: 'none',
              borderRadius: 8,
              color: '#0f0f1a',
              fontSize: 13,
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
            }}
          >
            <span style={{ fontSize: 16 }}>{'\uD83D\uDD2C'}</span>
            MAIA Research
          </button>
          <div style={{ fontSize: 13, color: '#9ca3af' }}>
            <span style={{ marginRight: 8 }}>Strategist:</span>
            <span style={{ fontWeight: '500', color: '#f3f4f6' }}>Trend Strategist</span>
          </div>
          <div style={{
            padding: '6px 12px',
            backgroundColor: '#252540',
            borderRadius: 6,
            fontSize: 12,
            color: '#9ca3af'
          }}>
            HITL Mode Active
          </div>
        </div>
      </header>

      {/* Workflow Progress (only show when in workflow) */}
      {activeView === 'workflow' && <WorkflowProgress workflowStep={workflowStep} />}

      {/* Main Content */}
      <main>
        {activeView === 'dashboard' && <Dashboard state={state} />}
        {activeView === 'workflow' && renderWorkflowContent()}
        {activeView === 'package' && <PackageDetailView state={state} />}
      </main>

      {/* MAIA Modal */}
      {showMaia && selectedTrend && <MaiaModal state={state} />}

      {/* Audience Brief Modal */}
      <AudienceBriefModal state={state} />

      {/* Quick MAIA Research Modal */}
      {showQuickMaia && <QuickMaiaModal state={state} />}

      {/* Notification (replaces alert() calls) */}
      <Notification message={notification} onClose={clearNotification} />
    </div>
  );
};

export default InsightsAgentPrototype;
